// client/src/features/vendorInvoices/processingIssue.tsx
//
// Why a vendor invoice's processing failed, worded for the person. The one owner of these
// sentences: the opened row's "Issue:" box and the list row both read it. The stored
// `lastError` is the extractor's own text, so it is matched to a fixed sentence and never shown.
//
// B031 (Tidal bug report): Reprocess runs in the background, and a failure showed only a
// "Failed" pill on the list row; the reason sat inside the opened row, where nobody scanning
// the list looked. The row now carries it too (ProcessingIssueNote).
import React from 'react';
import type { VendorInvoiceStatus } from '../../lib/vendorInvoiceApi';

export function processingIssueText(lastError: string | null | undefined): string | null {
  const raw = String(lastError ?? '').trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (raw.includes('429') || lower.includes('rate limit'))
    return 'Processing was rate-limited. Please click Reprocess in a few minutes.';
  // An auth failure is permanent and not user-fixable. Falling
  // through to the generic message told people to click
  // Reprocess, which can only ever fail the same way — so the
  // real problem (a bad or expired key) stayed invisible.
  if (
    raw.includes('401') ||
    lower.includes('invalid api key') ||
    lower.includes('unauthorized') ||
    lower.includes('unauthenticated')
  )
    return 'The AI extraction service rejected our credentials (invalid or expired API key). Reprocessing will not help — please ask an administrator to check the MISTRAL_API_KEY setting.';
  if (lower.includes('missing env var') || lower.includes('missing mistral_api_key'))
    return 'Processing failed due to missing configuration. Please contact admin.';
  return 'Processing failed. Please click Reprocess or contact admin if it keeps failing.';
}

/** The reason under a failed invoice's status pill on the list. Nothing for any other status. */
export const ProcessingIssueNote: React.FC<{ status: VendorInvoiceStatus; lastError?: string | null }> = ({
  status,
  lastError,
}) => {
  const text = status === 'failed' ? processingIssueText(lastError) : null;
  if (!text) return null;
  return (
    <div className="mt-1 max-w-[260px] text-xs text-rose-700 line-clamp-2" title={text}>
      {text}
    </div>
  );
};
